/**
 * PRD.330 Phase 2 — Viewport-aware placement for report filter popovers.
 *
 * Same portal + getBoundingClientRect approach as DateChipPopover, plus the
 * collision handling that pattern lacks: flip above when there is no room
 * below, right-align near the right edge, clamp inside the viewport with a
 * margin, and cap height (the popover body scrolls) on very short viewports.
 */

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PopoverPlacement {
  top: number;
  left: number;
  maxHeight: number;
  placement: 'below' | 'above';
}

export function computePopoverPosition(
  anchor: AnchorRect,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  opts: { margin?: number; gap?: number } = {},
): PopoverPlacement {
  const margin = opts.margin ?? 8;
  const gap = opts.gap ?? 4;
  const maxHeight = Math.max(120, viewport.height - margin * 2);
  const height = Math.min(size.height, maxHeight);

  // Horizontal: start at the anchor's left edge; if that overflows, align the
  // popover's right edge with the anchor's right edge; then clamp.
  let left = anchor.left;
  if (left + size.width > viewport.width - margin) left = anchor.right - size.width;
  left = Math.max(margin, Math.min(left, viewport.width - size.width - margin));

  // Vertical: prefer below; flip above when below doesn't fit and above has more room.
  const spaceBelow = viewport.height - anchor.bottom - gap - margin;
  const spaceAbove = anchor.top - gap - margin;
  const placement: 'below' | 'above' =
    height <= spaceBelow || spaceBelow >= spaceAbove ? 'below' : 'above';
  let top = placement === 'below' ? anchor.bottom + gap : anchor.top - gap - height;
  top = Math.max(margin, Math.min(top, viewport.height - height - margin));

  return { top, left, maxHeight, placement };
}
